import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Play, Pause, SpeakerHigh, FileArrowDown, Microphone } from "@phosphor-icons/react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

function AuthedMediaBlob({ url, mime, children }) {
  // Fetches /api/media/{id} with the auth header via axios and converts to a blob URL
  const [blobUrl, setBlobUrl] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    if (!url) return;
    let revoke = null;
    api
      .get(url.replace(/^\/api/, ""), { responseType: "blob" })
      .then((r) => {
        const b = new Blob([r.data], { type: mime || r.headers?.["content-type"] });
        const u = URL.createObjectURL(b);
        revoke = u;
        setBlobUrl(u);
      })
      .catch(() => setErr(true));
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [url, mime]);
  if (err) return <div className="text-[11px] text-[#F97316]">No pude cargar el adjunto</div>;
  if (!blobUrl) return <div className="text-[11px] text-[#71717A]">Cargando adjunto…</div>;
  return children(blobUrl);
}

function AudioPlayer({ url, mime, isCustomer }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [cur, setCur] = useState(0);

  const fmt = (s) => {
    if (!s || !isFinite(s)) return "0:00";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) a.play();
    else a.pause();
  };

  return (
    <AuthedMediaBlob url={url} mime={mime}>
      {(blobUrl) => (
        <div className={`flex items-center gap-3 rounded-xl p-2.5 min-w-[220px] ${
          isCustomer ? "bg-[#18181B]/70" : "bg-black/20"
        }`}>
          <button
            type="button"
            onClick={toggle}
            data-testid="audio-play-btn"
            className={`h-10 w-10 rounded-full flex items-center justify-center shrink-0 transition-colors ${
              playing ? "bg-[#F97316] text-white" : "bg-[#5E6AD2] text-white hover:bg-[#4F58B8]"
            }`}
            aria-label={playing ? "Pausar" : "Reproducir"}
          >
            {playing ? <Pause size={16} weight="fill" /> : <Play size={16} weight="fill" />}
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 mb-1 text-[10px] uppercase tracking-wider opacity-75">
              <Microphone size={10} /> Nota de voz
            </div>
            <div className="h-1 w-full rounded-full bg-white/15 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-[#5E6AD2] to-[#F97316] transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="flex items-center justify-between mt-1 text-[9px] font-mono opacity-70">
              <span>{fmt(cur)}</span>
              <span>{fmt(duration)}</span>
            </div>
          </div>
          <audio
            ref={audioRef}
            src={blobUrl}
            preload="metadata"
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
            onEnded={() => { setPlaying(false); setProgress(0); setCur(0); }}
            onLoadedMetadata={(e) => setDuration(e.target.duration)}
            onTimeUpdate={(e) => {
              const a = e.target;
              setCur(a.currentTime);
              setProgress(a.duration ? (a.currentTime / a.duration) * 100 : 0);
            }}
          />
        </div>
      )}
    </AuthedMediaBlob>
  );
}

function ImageViewer({ url, mime }) {
  const [open, setOpen] = useState(false);
  return (
    <AuthedMediaBlob url={url} mime={mime}>
      {(blobUrl) => (
        <>
          <button
            type="button"
            onClick={() => setOpen(true)}
            data-testid="image-thumb-btn"
            className="block group"
          >
            <img
              src={blobUrl}
              alt="Adjunto"
              className="max-w-[240px] max-h-[240px] rounded-lg object-cover border border-black/20 group-hover:opacity-90 transition-opacity"
            />
          </button>
          {open && (
            <div
              className="fixed inset-0 z-[200] bg-black/90 flex items-center justify-center p-6 cursor-zoom-out"
              onClick={() => setOpen(false)}
              data-testid="image-lightbox"
            >
              <img src={blobUrl} alt="Adjunto" className="max-h-full max-w-full rounded-lg" />
            </div>
          )}
        </>
      )}
    </AuthedMediaBlob>
  );
}

/**
 * Renders a message bubble with text + optional image / audio / video / document.
 * Props:
 *   message = { id, text, direction, sender, created_at, media_url, media_type, media_mime }
 *   align = "left" | "right"
 *   variant = "customer" | "operator" | "ai"
 */
export default function MessageBubble({ message: m, align = "left", variant = "customer", compact = false }) {
  const isCustomer = variant === "customer";
  const isAI = variant === "ai";
  const url = m.media_url;
  const mt = m.media_type;
  const mime = m.media_mime;
  const textLooksLikeTag = m.text && /^\[(image|audio|voice|video|documento|nota de voz)\]/i.test(m.text);
  const showText = m.text && !textLooksLikeTag;

  return (
    <div className={`flex ${align === "right" ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
      <div
        className={`max-w-[85%] ${compact ? "px-3 py-2 text-xs" : "px-3.5 py-2.5 text-sm"} rounded-2xl ${
          align === "right" ? "rounded-tr-sm" : "rounded-tl-sm"
        } ${
          isCustomer
            ? "bg-[#18181B] border border-[#27272A] text-white"
            : isAI
            ? "bg-[#1A1B3A] text-white ai-glow"
            : "bg-[#5E6AD2] text-white"
        }`}
      >
        {url && (mt === "image") && <ImageViewer url={url} mime={mime} />}
        {url && (mt === "audio" || mt === "voice") && (
          <AudioPlayer url={url} mime={mime} isCustomer={isCustomer} />
        )}
        {url && mt === "video" && (
          <AuthedMediaBlob url={url} mime={mime}>
            {(blobUrl) => (
              <video src={blobUrl} controls className="max-w-[280px] max-h-[280px] rounded-lg" />
            )}
          </AuthedMediaBlob>
        )}
        {url && mt === "document" && (
          <AuthedMediaBlob url={url} mime={mime}>
            {(blobUrl) => (
              <a
                href={blobUrl}
                download
                className="flex items-center gap-2 text-xs underline"
              >
                <FileArrowDown size={14} /> Descargar documento
              </a>
            )}
          </AuthedMediaBlob>
        )}
        {showText && <div className={`${url ? "mt-2" : ""} whitespace-pre-wrap break-words`}>{m.text}</div>}
        {!showText && !url && textLooksLikeTag && (
          <div className="flex items-center gap-1.5 text-[11px] opacity-70 italic">
            <SpeakerHigh size={12} /> {m.text}
          </div>
        )}
        <div className={`${compact ? "text-[9px]" : "text-[10px]"} mt-1 opacity-60 font-mono flex items-center gap-1 justify-end`}>
          {new Date(m.created_at).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })}
          {isAI && <span>· IA</span>}
        </div>
      </div>
    </div>
  );
}
